import { addNutrition, emptyNutrition, nutritionOf, scaleNutrition } from './nutrition';
import type { NutritionTotals } from './nutrition';
import { productOf } from './price-book';
import type { DietTag, NutritionRow, Product } from './types';

const FISH_TAGS: readonly DietTag[] = ['fish', 'seafood'];

/**
 * DE: Zutat eines Rezepts; die Menge gilt für die Grundportionen des Rezepts.
 * EN: Recipe ingredient; the quantity is for the recipe's base servings.
 */
export interface Ingredient {
  productId: string;
  quantity: number;
}

/**
 * DE: Küche eines Rezepts; `other` nur für Frühstücke ohne klare Zuordnung.
 * EN: Cuisine of a recipe; `other` only for breakfasts without a clear match.
 */
export type Cuisine = 'asian' | 'ukrainian' | 'italian' | 'german' | 'other';

export interface Recipe {
  id: string;
  /**
   * DE: Name auf Ukrainisch; bei eigenen Rezepten so, wie er eingegeben wurde.
   * EN: Name in Ukrainian; for own recipes as it was typed.
   */
  name: string;
  /**
   * DE: Deutscher Name (eingebaute Rezepte); fehlt er, wird `name` gezeigt.
   * EN: German name (built-in recipes); without it `name` is shown.
   */
  nameDe?: string;
  cuisine: Cuisine;
  meal: 'breakfast' | 'dinner';
  activeMinutes: number;
  totalMinutes: number;
  baseServings: number;
  ingredients: Ingredient[];
  steps: string[];
  /**
   * DE: Deutsche Arbeitsschritte, gleich viele wie `steps`.
   * EN: German steps, as many as `steps`.
   */
  stepsDe?: string[];
  /**
   * DE: Lässt sich gut aufwärmen oder kalt essen, also als Mittagessen mitnehmen.
   * EN: Reheats well or tastes good cold, so it can be taken to work for lunch.
   */
  lunchboxFriendly: boolean;
  /**
   * DE: Schnelles Notfallgericht nur aus lange haltbaren Zutaten.
   * EN: Quick backup dinner made only from long-lasting ingredients.
   */
  backupKit: boolean;
  meatAddOn?: { productId: string; quantityPerPortion: number };
  own?: boolean;
}

/**
 * DE: Schnell genug für müde Abende: aktive Arbeit und Gesamtzeit innerhalb der Grenzen.
 * EN: Quick enough for tired evenings: active work and total time within the limits.
 */
export function isQuick(
  recipe: Recipe,
  limits: { activeMinutes: number; totalMinutes: number },
): boolean {
  return recipe.activeMinutes <= limits.activeMinutes && recipe.totalMinutes <= limits.totalMinutes;
}

/**
 * DE: Gericht mit Fisch oder Meeresfrüchten; Fleischbeilagen zählen nicht.
 * EN: Dish with fish or seafood; meat add-ons do not count.
 */
export function isFishDinner(recipe: Recipe, catalog: ReadonlyMap<string, Product>): boolean {
  return recipe.ingredients.some(
    (ingredient) =>
      productOf(catalog, ingredient.productId).tags?.some((tag) => FISH_TAGS.includes(tag)) ??
      false,
  );
}

/**
 * DE: Nährwerte einer Grundportion (alle Zutaten inklusive Grundzutaten).
 * EN: Nutrition of one base portion (all ingredients including staples).
 */
export function portionNutrition(
  recipe: Recipe,
  catalog: ReadonlyMap<string, Product>,
  nutrition: Readonly<Record<string, NutritionRow>>,
): NutritionTotals {
  const total = recipe.ingredients.reduce((sum, ingredient) => {
    const row = nutrition[ingredient.productId];
    if (!row) throw new Error(`No nutrition data for ${ingredient.productId}`);
    const product = productOf(catalog, ingredient.productId);
    return addNutrition(sum, nutritionOf(row, product.unit, ingredient.quantity));
  }, emptyNutrition());
  return scaleNutrition(total, 1 / recipe.baseServings);
}
